import type {
  FmOperationalActionDescriptor,
  FmOperationalBlockerDescriptor,
  FmOperationalPaidBoundaryDescriptor,
  FmOperationalRegionDescriptor,
  FmOperationalStageDescriptor,
  FmOperationalWorkspaceDescriptor,
} from './FmOperationalWorkspaceRoot.types';
import { regionServesContent } from './FmOperationalWorkspaceRegions.helpers';

/**
 * What the one authoritative status rail is currently reporting. Derived, never
 * served as a literal: a pending mutation or a non-current revision owns the
 * rail, so it can never read "ready" while the workspace is saving, deleting,
 * or conflicted.
 */
export type FmOperationalStatusKey =
  | 'ready'
  | 'loading'
  | 'empty'
  | 'stale'
  | 'blocked'
  | 'error'
  | 'working'
  | 'conflict'
  | 'failed'
  | 'saved';

export interface FmOperationalStatusDescriptor {
  key: FmOperationalStatusKey;
  label: string;
}

/**
 * Resolve the status rail from mutation and revision state first, falling back
 * to the short served state token. The long `stateLabel` sentence is never the
 * rail value — it is the stage lede.
 */
export function operationalWorkspaceStatus(
  descriptor: FmOperationalWorkspaceDescriptor,
): FmOperationalStatusDescriptor {
  const mutation = descriptor.mutation;
  /* An in-flight or conflicted mutation outranks the resting state token in
     EVERY state. Reporting the state first hid a save that was still running on
     a stale or blocked workspace. */
  if (mutation.status === 'pending') return { key: 'working', label: mutation.label };
  if (mutation.status === 'conflict' || descriptor.revision.status === 'conflict') {
    return { key: 'conflict', label: mutation.label };
  }
  if (descriptor.state !== 'ready') {
    return {
      key: descriptor.state,
      label: descriptor.vocabulary.statuses[descriptor.state],
    };
  }
  if (mutation.status === 'failed') return { key: 'failed', label: mutation.label };
  if (mutation.status === 'succeeded') return { key: 'saved', label: mutation.label };
  return { key: 'ready', label: descriptor.vocabulary.statuses.ready };
}

/**
 * Regions still render unless nothing at all was served — and `loading` renders
 * them as SKELETONS rather than collapsing the whole body to one spinner in a
 * ~1345x300px void. The owner reads the shape of the
 * page while it fills, and 300px of void does not become 2600px of regions on
 * arrival.
 */
export function rendersRegions(descriptor: FmOperationalWorkspaceDescriptor): boolean {
  if (descriptor.state === 'error') return descriptor.error.failedRegionIds.length > 0;
  return true;
}

/** Region ids the given stage renders. */
function stageRegionIds(
  descriptor: FmOperationalWorkspaceDescriptor,
  stageId: string,
): readonly string[] {
  return descriptor.stages.find((stage) => stage.id === stageId)?.regionIds ?? [];
}

/**
 * Whether the scoped state surface renders somewhere on this stage. When it does
 * not — the owner navigated to a stage that owns neither the failing region nor
 * the blocked scope — the leading notice must carry it instead, or the workspace
 * shows an inhibited toolbar with no visible cause and no reachable resolving
 * action.
 */
export function scopedSurfaceRendersOnStage(
  descriptor: FmOperationalWorkspaceDescriptor,
  activeStageId: string,
): boolean {
  const rendered = stageRegionIds(descriptor, activeStageId);
  if (descriptor.state === 'error') {
    return descriptor.error.failedRegionIds.some((regionId) => rendered.includes(regionId));
  }
  if (descriptor.state !== 'blocked') return false;
  const scope = descriptor.blocked.scope;
  if (scope.kind === 'region') return rendered.includes(scope.regionId);
  /* A STAGE-scoped blocker has no in-place renderer — only a region owns one —
     so the leading notice always carries it. Claiming it "renders on this stage"
     because the ids matched suppressed the leading notice on exactly the stage
     the blocker applies to, and the at-cap specimen therefore showed the owner a
     grey lede and a greyed control with no notice, no named blocker and no
     resolving action anywhere on the surface. Region
     and workspace scope were unaffected, which is why it survived 6.16. */
  return false;
}

/**
 * Whether one region is itself the failing or blocked scope, and therefore owns
 * an in-place state surface instead of its served content. `null` means the
 * region renders normally.
 */
export function regionStateKind(
  descriptor: FmOperationalWorkspaceDescriptor,
  regionId: string,
): 'error' | 'blocked' | null {
  if (descriptor.state === 'error' && descriptor.error.failedRegionIds.includes(regionId)) {
    return 'error';
  }
  if (
    descriptor.state === 'blocked' &&
    descriptor.blocked.scope.kind === 'region' &&
    descriptor.blocked.scope.regionId === regionId
  ) {
    return 'blocked';
  }
  return null;
}

/** Stage that owns a blocker's scope, so the blocker inhibits only that stage. */
export function blockedStageId(
  descriptor: FmOperationalWorkspaceDescriptor,
  blocker: FmOperationalBlockerDescriptor,
): string | null {
  if (blocker.scope.kind === 'workspace') return null;
  if (blocker.scope.kind === 'stage') return blocker.scope.stageId;
  const regionId = blocker.scope.regionId;
  return descriptor.stages.find((stage) => stage.regionIds.includes(regionId))?.id ?? null;
}

/** The single control the current state surface renders, if any. */
export function stateSurfaceActionId(descriptor: FmOperationalWorkspaceDescriptor): string | null {
  if (descriptor.state === 'empty') return descriptor.empty.primaryActionId;
  if (descriptor.state === 'stale') return descriptor.stale.refreshActionId;
  if (descriptor.state === 'error') return descriptor.error.retryActionId;
  if (descriptor.state === 'blocked') return descriptor.blocked.resolvingActionId;
  return null;
}

/**
 * Control ids the renderer places inside a state surface or a blocker receipt.
 * They lead their own surface, so the stage toolbar must not repeat them.
 */
export function stateOwnedActionIds(
  descriptor: FmOperationalWorkspaceDescriptor,
): ReadonlySet<string> {
  const ids = new Set<string>();
  if (descriptor.state === 'empty') ids.add(descriptor.empty.primaryActionId);
  if (descriptor.state === 'stale') ids.add(descriptor.stale.refreshActionId);
  if (descriptor.state === 'error') ids.add(descriptor.error.retryActionId);
  if (descriptor.state === 'blocked') ids.add(descriptor.blocked.resolvingActionId);
  for (const receipt of descriptor.mutation.receipts) {
    if (receipt.kind === 'blocker') ids.add(receipt.resolvingActionId);
  }
  /* A control a REGION renders is rendered there. A destination card and a paid
     boundary card each carry their own CTA, so repeating them in the stage
     toolbar would print the same action twice under two different weights — and
     would push the commit toolbar to six buttons, past the four
     operations-planning.md acceptance criterion 6 allows before it wraps. */
  for (const boundary of descriptor.paidBoundaries) ids.add(boundary.preview.unlockActionId);
  for (const region of descriptor.regions) {
    if (region.kind === 'handoff') {
      for (const destination of region.destinations) ids.add(destination.actionId);
    }
  }
  return ids;
}

/**
 * Controls an unavailable region of the shown stage carries on its own notice.
 * The explanation and its fix read as one unit there, so the stage toolbar
 * does not repeat the control a column away from the sentence that needs it.
 */
export function regionNoticeActionIds(
  descriptor: FmOperationalWorkspaceDescriptor,
  stage: FmOperationalStageDescriptor,
): ReadonlySet<string> {
  const ids = new Set<string>();
  if (descriptor.state === 'loading') return ids;
  for (const region of descriptor.regions) {
    const resolving = region.unavailable.resolvingActionId;
    if (!resolving || !stage.regionIds.includes(region.id)) continue;
    if (!regionServesContent(descriptor, region)) ids.add(resolving);
  }
  return ids;
}

/** Resolve a declared control by id; the validator guarantees it exists. */
export function operationalControl(
  descriptor: FmOperationalWorkspaceDescriptor,
  actionId: string,
): FmOperationalActionDescriptor | undefined {
  return descriptor.controls.find((control) => control.id === actionId);
}

export function actionClick(
  action: FmOperationalActionDescriptor,
  descriptor: FmOperationalWorkspaceDescriptor,
  setOpenDialogId: (id: string | null) => void,
  inhibited: boolean,
): void {
  if (inhibited || action.disabled) return;
  if (action.confirmationId) {
    setOpenDialogId(action.confirmationId);
    return;
  }
  descriptor.onEvent?.({
    kind: 'action',
    actionId: action.id,
    expectedRevisionId: action.expectedRevisionId,
  });
}

/**
 * Every paid boundary whose scope is the given surface. `stage` matches only the
 * stage the owner is on, so a card cannot follow them to a stage that does not
 * withhold anything.
 */
export function paidBoundariesFor(
  descriptor: FmOperationalWorkspaceDescriptor,
  scope: { kind: 'leading'; activeStageId: string } | { kind: 'region'; regionId: string },
): readonly FmOperationalPaidBoundaryDescriptor[] {
  return descriptor.paidBoundaries.filter((boundary) => {
    if (scope.kind === 'region') {
      return boundary.scope.kind === 'region' && boundary.scope.regionId === scope.regionId;
    }
    if (boundary.scope.kind === 'workspace') return true;
    return boundary.scope.kind === 'stage' && boundary.scope.stageId === scope.activeStageId;
  });
}

/**
 * The stage's regions in reading order for the tier, and the region a guided
 * outcome follows: what the choices add up to reads right after the step's own
 * work (the dominant region), before supporting regions such as what is left out.
 */
export function stageRegionPlan(
  descriptor: FmOperationalWorkspaceDescriptor,
  activeStage: FmOperationalStageDescriptor,
  tier: string,
): { stageRegions: FmOperationalRegionDescriptor[]; outcomeAfterRegionId: string | null } {
  const stageRegions = rendersRegions(descriptor)
    ? (tier === 'mobile' ? activeStage.mobileRegionOrder : activeStage.regionIds)
        .map((regionId) => descriptor.regions.find((region) => region.id === regionId))
        .filter((region): region is FmOperationalRegionDescriptor => Boolean(region))
    : [];
  const outcomeAfterRegionId =
    descriptor.guidance?.summary?.outcome &&
    stageRegions.some((region) => region.id === activeStage.dominantRegionId)
      ? activeStage.dominantRegionId
      : null;
  return { stageRegions, outcomeAfterRegionId };
}
